import React, { useState, useEffect } from 'react';
import { HiOutlinePencilSquare } from 'react-icons/hi2';
import Modal from 'react-modal';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import './notes.css'; // Small-screen responsive styles (desktop unchanged)

// Register the image resize module
ReactQuill.Quill.register('modules/imageResize', ImageResize);

// Bind modal to your app element (for accessibility)
Modal.setAppElement('#root');

function Notes({ onDataChange }) {
  const [subject, setSubject] = useState('Default Subject');
  const [recipient, setRecipient] = useState('Default Recipient');
  const [tags, setTags] = useState([]);
  const [buttonClickCount, setButtonClickCount] = useState({
    tryIn: 0,
    emerg: 0,
    nextCase: 0,
    pickup: 0,
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [editorHtml, setEditorHtml] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const formData = {
      subject,
      recipient,
      tags,
      buttonClickCount,
      attachedFiles,
      editorHtml,
    };
    if (onDataChange) {
      onDataChange(formData);
    }
  }, [subject, recipient, tags, buttonClickCount, attachedFiles, editorHtml, onDataChange]);

  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
  };

  const handleRecipientChange = (event) => {
    setRecipient(event.target.value);
  };

  const handleButtonClick = (buttonName) => {
    setButtonClickCount((prevState) => ({
      ...prevState,
      [buttonName]: prevState[buttonName] + 1,
    }));
  };

  const isButtonGreen = (buttonName) => buttonClickCount[buttonName] % 2 === 1;

  const handleFileChange = (event) => {
    const files = event.target.files;
    setAttachedFiles((prevAttachedFiles) => [
      ...prevAttachedFiles,
      ...Array.from(files),
    ]);
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prevAttachedFiles) => {
      const newAttachedFiles = [...prevAttachedFiles];
      newAttachedFiles.splice(index, 1);
      return newAttachedFiles;
    });
  };

  const handleAddTag = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setTags((prevTags) => {
        const newTag = event.target.value.trim();
        if (!prevTags.includes(newTag)) {
          return [...prevTags, newTag];
        }
        return prevTags;
      });
      event.target.value = ''; // Clear input field after adding the tag
    }
  };

  const handleRemoveTag = (index) => {
    setTags((prevTags) => {
      const newTags = [...prevTags];
      newTags.splice(index, 1);
      return newTags;
    });
  };

  const handleEditorChange = (html) => {
    setEditorHtml(html);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditorHtml('');
    setSubject('Default Subject');
    setRecipient('Default Recipient');
  };

  const saveNote = () => {
    if (!editorHtml.trim()) {
      alert('Note cannot be empty');
      return;
    }

    const formattedNote = `
      <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 5px;">
        ${recipient} (for internal use only)<br/>
        Created: ${new Date().toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })} for ${subject}
      </div>
      ${editorHtml}
    `;

    setEditorHtml(formattedNote);
    setIsModalOpen(false);
  };

  // Styles (reused from your original code)
  const mainContainerStyle = {
    backgroundColor: 'white',
    padding: '10px',
  };

  const titleStyle = {
    backgroundColor: 'white',
    color: '#004aad',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  };

  const formContainerStyle = {
    border: '1px solid #ccc',
    padding: '20px',
    borderTop: '3px solid #004aad',
    backgroundColor: '#f4f4f4',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  };

  const columnStyle = {
    flex: '1',
    minWidth: '300px',
  };

  const selectContainerStyle = {
    display: 'flex',
    gap: '5px',
    marginBottom: '15px',
  };

  const selectStyle = {
    borderRadius: '4px',
    fontSize: '19px',
    padding: '5px',
    border: '1px solid #ccc',
  };

  const buttonStyle = {
    marginRight: '4px',
    borderRadius: '4px',
    padding: '5px 10px',
    border: '1px solid #ccc',
    cursor: 'pointer',
    marginBottom: '10px',
  };

  const fileInputStyle = {
    marginBottom: '15px',
  };

  const fileListStyle = {
    listStyle: 'none',
    padding: '0',
  };

  const fileItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
  };

  const removeFileButtonStyle = {
    marginLeft: '10px',
    color: 'red',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  };

  const tagInputStyle = {
    marginLeft: '10px',
    borderRadius: '4px',
    padding: '5px',
    border: '1px solid #ccc',
    marginBottom: '10px',
  };

  const tagContainerStyle = {
    marginTop: '10px',
  };

  const tagItemStyle = {
    margin: '5px',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const tagStyle = {
    padding: '5px 10px',
    backgroundColor: '#f1f1f1',
    borderRadius: '20px',
    border: '1px solid #ccc',
  };

  const removeTagButtonStyle = {
    marginLeft: '5px',
    color: 'red',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  };

  // ReactQuill modules and formats (reused from CaseTab)
  const modules = {
    toolbar: [
      [{ header: '1' }, { header: '2' }, { font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'],
    },
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
  ];

  return (
    <div style={mainContainerStyle} className="notes-main">
      <h3 style={titleStyle} className="notes-title">
        <HiOutlinePencilSquare style={{ marginRight: '5px', color: '#004aad' }} />
        Notes
      </h3>
      <div style={formContainerStyle} className="notes-form-container">
        <div style={columnStyle} className="notes-column">
          <button
            onClick={openModal}
            className="notes-add-button"
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '8px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Add Note
          </button>
        </div>

        <div style={columnStyle} className="notes-column">
          {/* Buttons for handling actions */}
          <div className="notes-action-buttons">
            <button
              className="notes-action-button"
              style={{
                ...buttonStyle,
                backgroundColor: isButtonGreen('tryIn') ? 'green' : 'initial',
                color: isButtonGreen('tryIn') ? 'white' : 'inherit',
              }}
              onClick={() => handleButtonClick('tryIn')}
            >
              Try-In
            </button>
            <button
              className="notes-action-button"
              style={{
                ...buttonStyle,
                backgroundColor: isButtonGreen('emerg') ? 'green' : 'initial',
                color: isButtonGreen('emerg') ? 'white' : 'inherit',
              }}
              onClick={() => handleButtonClick('emerg')}
            >
              Emerg
            </button>
            <button
              className="notes-action-button"
              style={{
                ...buttonStyle,
                backgroundColor: isButtonGreen('nextCase') ? 'green' : 'initial',
                color: isButtonGreen('nextCase') ? 'white' : 'inherit',
              }}
              onClick={() => handleButtonClick('nextCase')}
            >
              Next-case
            </button>
            <button
              className="notes-action-button"
              style={{
                ...buttonStyle,
                backgroundColor: isButtonGreen('pickup') ? 'green' : 'initial',
                color: isButtonGreen('pickup') ? 'white' : 'inherit',
              }}
              onClick={() => handleButtonClick('pickup')}
            >
              Pickup
            </button>
          </div>

          {/* File Upload Input */}
          <div style={fileInputStyle} className="notes-file-input">
            <input type="file" multiple onChange={handleFileChange} />
          </div>

          {/* Displaying Attached Files */}
          {attachedFiles.length > 0 && (
            <div className="notes-files-section">
              <p>Attached Files:</p>
              <ul style={fileListStyle}>
                {attachedFiles.map((file, index) => (
                  <li key={index} style={fileItemStyle} className="notes-file-item">
                    <span className="notes-file-name">{file.name}</span>
                    <button
                      style={removeFileButtonStyle}
                      onClick={() => handleRemoveFile(index)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tags Section */}
          <div style={{ marginTop: '30px', marginBottom: '30px' }} className="notes-tags-section">
            <div>
              <input
                type="text"
                onKeyDown={handleAddTag}
                style={tagInputStyle}
                className="notes-tag-input"
                placeholder="Enter tag and press Enter"
              />
            </div>

            <div style={tagContainerStyle}>
              {tags.length > 0 && (
                <div>
                  <p>Selected Tags:</p>
                  <div>
                    {tags.map((tag, index) => (
                      <span key={index} style={tagItemStyle}>
                        <span style={tagStyle}>{tag}</span>
                        <button
                          onClick={() => handleRemoveTag(index)}
                          style={removeTagButtonStyle}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Rich Text Editor */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        portalClassName="ReactModalPortal notes-modal-portal"
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '800px',
            maxWidth: '90%',
            padding: '20px',
            borderRadius: '8px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          },
        }}
        contentLabel="Add Rich Note Modal"
      >
        <h2 style={{ color: '#004aad', marginBottom: '20px' }} className="notes-modal-title">
          Add Note
        </h2>
        <div style={{ marginBottom: '20px' }}>
          <div style={selectContainerStyle} className="notes-select-container">
            <select
              value={subject}
              onChange={handleSubjectChange}
              style={selectStyle}
              className="notes-select"
            >
              <option value="Default Subject">Select Subject</option>
              <option value="case shipping">Case Shipping</option>
              <option value="case fabrication">Case Fabrication</option>
              <option value="case invoice">Case Invoice</option>
            </select>
            <select
              value={recipient}
              onChange={handleRecipientChange}
              style={selectStyle}
              className="notes-select"
            >
              <option value="Default Recipient">Select Recipient</option>
              <option value="accounts">Accounts</option>
              <option value="lab">Lab</option>
              <option value="admin SM">Admin SM</option>
              <option value="admin SE">Admin SE</option>
            </select>
          </div>
          <div
            className="notes-editor-wrapper"
            style={{
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff',
              minHeight: '300px',
            }}
          >
            <ReactQuill
              theme="snow"
              value={editorHtml}
              onChange={handleEditorChange}
              modules={modules}
              formats={formats}
              placeholder="Start typing your notes..."
              style={{ height: '250px' }}
            />
          </div>
        </div>
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
          className="notes-modal-actions"
        >
          <button
            onClick={closeModal}
            style={{
              backgroundColor: '#f0f0f0',
              color: '#333',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={saveNote}
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            disabled={!editorHtml.trim()}
          >
            Save Note
          </button>
        </div>
      </Modal>
    </div>
  );
}

Notes.modules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize'],
  },
};

Notes.formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
];

export default Notes;